import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SettingsService } from '../settings/settings.service';
import { PdfStrokeColor, AnnotationTypes, CustomDocviewerCursorStyles, PdfToolType } from 'src/app/core/pdf-utils/enums';
import { ISettings, ICustomTextNoteElStyle, ICustomTextAreaState, ITextNoteFont } from 'src/app/core/interfaces';
import { TextNotesFontTypeStyle } from 'src/app/core/enums';
import { DocumentManagerService } from '../document/document-manager.service';
import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { createGuid, updateCursorIcon, isAnnotationType, resetCursorIcon } from 'src/app/core/common';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { CurrentUIState, uiState } from 'src/models/UIState';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { DocumentToolsService, ToolEventSource } from '../document/document-tools.service';
import { CommandInvokerService } from '../command/command-invoker.service';
import { TextAnnotationCommand } from '../command/pdf-commands/text-annotation.command';
import { AutosaveManagerService } from '../autosave/autosave-manager.service';
// import { NoteCreationMode } from 'src/models/NoteCreationMode';
import { DefaultSettings } from '../settings/defaultSettingsData';
import { CloudMimeTypesEnum } from 'src/app/utils/enums/cloud-mime-types.enum';
import { Core } from '@pdftron/webviewer';
import { GoogleAnalyticsActions, GoogleAnalyticsService } from '../google-analytics.service';

export const commonWrapper = 'common-wrapper-frame';
export const frameId = 'custom-input-frame';
export const frameCursorId = 'common-wrapper-frame-cursor-id';
export const editorCls = 'frame-wrapper';
export const editorId = 'text-editor';

@Injectable({
  providedIn: 'root'
})
export class CustomTextareaService {
  // private noteCreationMode = NoteCreationMode.SINGLE;
  // private notesCreated = 0;

  private readonly frameId: string = frameId;
  private readonly commonWrapper: string = commonWrapper;
  private readonly frameCursorId: string = frameCursorId;
  private readonly editorId: string = editorId;

  private settings: ISettings | null = null;
  private state: ICustomTextAreaState | null = null;
  private readonly stateSubj: BehaviorSubject<ICustomTextAreaState | null> = new BehaviorSubject<ICustomTextAreaState | null>(null);
  private style: ICustomTextNoteElStyle | null = null;

  textAreaState: BehaviorSubject<ICustomTextAreaState | null> = new BehaviorSubject<ICustomTextAreaState | null>(null);
  textAreaStyleState: BehaviorSubject<ICustomTextNoteElStyle | null> = new BehaviorSubject<ICustomTextNoteElStyle | null>(null);

  readonly documentId: BehaviorSubject<string> = new BehaviorSubject<string>(createGuid());

  private isActive: boolean = false;

  get isAreaActive(): boolean {
    return this.isActive;
  }

  private AsgardFeatureNames = AsgardFeatureNames;
  private hideTextAnot: boolean = false;
  private openedToolbar: boolean = false;

  // get creationMode(): NoteCreationMode {
  //   return this.noteCreationMode;
  // }
  // set creationMode(value: NoteCreationMode) {
  //   // this.notesCreated = 0;
  //   this.noteCreationMode = value;
  // }

  private get currentDoc(): AsgardDocPdf | null {
    const doc = this.docMgrService.currentDoc;
    if (doc) {
      if (doc instanceof AsgardDocPdf) {
        return doc;
      }
    }
    return null;
  }

  private get annotationManager(): Core.AnnotationManager | null {
    const doc = this.currentDoc;
    if (doc) {
      return doc.annotationManager;
    }
    return null;
  }

  private get frame(): HTMLIFrameElement | undefined {
    const el = (document.getElementById(this.frameId) as HTMLIFrameElement);

    if (el) {
      return el;
    }
  }

  private get commonWrapperEl(): HTMLDivElement {
    return (document.getElementById(this.commonWrapper) as HTMLDivElement);
  }

  private get frameWin(): Window | null | undefined {
    if (this.frame) {
      return this.frame.contentWindow;
    }
  }

  private get frameDoc(): Document | undefined {
    if (this.frameWin) {
      return this.frameWin.document;
    }
  }

  get input(): HTMLElement | null {
    if (this.frameDoc && this.frameDoc.body && this.frameDoc.body.children[0]) {
      return this.frameDoc.body.children[0] as HTMLElement;
    }
    return null;
  }

  set inputStyle(s: Partial<ICustomTextNoteElStyle>) {
    let pt: number | null = null;
    for (const key in s) {
      if (s[key]) {
        if (this.input) {
          const val = s[key];
          if (key === 'fontSize' && val) {
            pt = val * (this.state && this.state.zoom ? this.state.zoom : 1);
            this.input.style[key] = pt + 'px';
          } else if (key === 'fontFamily' && val) {
            this.input.style[key] = val.value;
          } else {
            this.input.style[key] = s[key];
          }
        }
      }
    }
    if (this.input) {
      if (this.activeNote === AnnotationTypes.StickyNote) {
        this.input.style.background = PdfStrokeColor.Yellow;
        this.input.style.border = (this.state?.zoom && this.state.zoom > 1 ? this.state.zoom : 1) + 'px solid black';
      } else {
        this.input.style.background = '';
        if (this.style && this.style.color) {
          this.input.style.border = `1px solid ${this.style.color}`;
        } else {
          this.input.style.border = 'none';
        }
      }
      this.updateSize();
    }
  }

  private get currentState(): ICustomTextAreaState | null {
    if (this.input === null) {
      return null;
    }

    const rect = this.commonWrapperEl.getBoundingClientRect();
    const { width, height } = this.input.getBoundingClientRect();

    return {
      style: this.style,
      text: this.input.innerText,
      x: rect.left,
      y: rect.top,
      width,
      height,
      pageIdx: null,
      restrictions: null,
      zoom: null,
      annotType: this.activeNote
    };
  }

  get docIdSubj(): Observable<string> {
    return this.documentId.asObservable();
  }

  get docId(): string {
    return this.documentId.getValue();
  }

  get activeNote(): AnnotationTypes | null {
    return this.activeNoteType.getValue();
  }

  set activeNote(e: AnnotationTypes | null) {
    this.activeNoteType.next(e);
  }

  public get getStateSubj(): Observable<ICustomTextAreaState | null> {
    return this.stateSubj.asObservable();
  }

  private activeNoteType: BehaviorSubject<AnnotationTypes | null> = new BehaviorSubject(null);
  // private isEdit: boolean = false;

  constructor(
    private readonly settingsService: SettingsService,
    private readonly docToolsService: DocumentToolsService,
    private readonly docMgrService: DocumentManagerService,
    private readonly ngzone: NgZone,
    private readonly uiService: CurrentUiStoreService,
    private readonly commandInvoker: CommandInvokerService,
    private readonly autosave: AutosaveManagerService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
  ) {
    this.initSubscriptions();
  }

  commonTextNotesClicksHandler = (
    e: MouseEvent | null = null,
  ): void => {
    this.ngzone.run(async () => {
      if (e && this.activeNote) {
        let clickedAnot = false;
        clickedAnot = this.isAnotClicked(e);
        if (!clickedAnot) {
          this.createNoteIfNeeded(e, false, this.activeNote);
        }
      } else if (e === null) {
        await this.removeFocusDoc();
      }
    });
  }

  async applyAndCreateNote(coords: { x: number, y: number }): Promise<void> {
    // console.warn('applyAndCreateNote', this.isActive);
    await this.removeFocusDoc();

    const wrapperBounds = this.commonWrapperEl.getBoundingClientRect();
    const mouseEvent = new MouseEvent(
      'click',
      {
        clientX: coords.x + wrapperBounds.left,
        clientY: coords.y + wrapperBounds.top
      }
    );
    // console.warn('before create', this.isActive);
    this.createNoteIfNeeded(mouseEvent, true, this.activeNote);
  }

  private initSubscriptions(): void {
    this.docMgrService.currentDocumentChanged.subscribe(async e => {
      if (e) {
        this.addNotesCursorStyles();
      }
    });

    this.uiService.getUIState().subscribe(async (e: CurrentUIState) => {
      if (e) {
        // if textnote toolbar was opened and now its closed
        // reset text notes styles to default one from user settings
        if (this.openedToolbar && !e[this.AsgardFeatureNames.TOOLBAR_MENU].active) {
          this.setDefaultStyles();
        }
        // first check if toolbar with text notes menu is open
        this.openedToolbar = e[this.AsgardFeatureNames.TOOLBAR_MENU].active;

        // find what type of text notes is active
        this.isActive = e[this.AsgardFeatureNames.TEXT_NOTES].active || e[this.AsgardFeatureNames.STICKY_NOTES].active;
        if (this.isActive) {
          this.activeNote = e[this.AsgardFeatureNames.TEXT_NOTES].active ? AnnotationTypes.FreeText : e[this.AsgardFeatureNames.STICKY_NOTES].active ? AnnotationTypes.StickyNote : null;
        }
        // console.warn('getUIState activeNote: ', this.activeNote);
        // console.warn('uiState[AsgardFeatureNames.NOTE_CREATION_MODE_SINGLE].active', uiState[AsgardFeatureNames.NOTE_CREATION_MODE_SINGLE].active);
        // console.warn('uiState[AsgardFeatureNames.NOTE_CREATION_MODE_MULTI].active', uiState[AsgardFeatureNames.NOTE_CREATION_MODE_MULTI].active);

        // console.log(this.isActive, 'this.isActive');
        // update cursor in any case
        this.addNotesCursorStyles();

        const pdfDocs = this.docMgrService.getAllDocuments(CloudMimeTypesEnum.ApplicationPdf) as AsgardDocPdf[];

        if (pdfDocs.length > 0 && this.hideTextAnot !== e[this.AsgardFeatureNames.SHOW_TEXT_ANNOTATIONS].active) {
          this.hideTextAnot = e[this.AsgardFeatureNames.SHOW_TEXT_ANNOTATIONS].active;
          pdfDocs.forEach((doc) => doc.showHideTextAnnotations(!this.hideTextAnot, [AnnotationTypes.FreeText, AnnotationTypes.StickyNote]));
        }
      }
    });

    this.getStateSubj.subscribe(async (e: ICustomTextAreaState | null) => {
      this.state = e;

      if (this.state && this.state.style && this.input) {
        this.input.innerText = this.state.text || '';
        this.inputStyle = this.state.style;
        this.saveFontSettings(this.state.style);
        this.updateSize();
      }

      this.textAreaState.next(this.state);
    });

    this.textAreaStyleStatus().subscribe(e => {
      if (e) {
        this.style = e;
        this.inputStyle = this.style;
        this.focus();
      }
    });

    this.settingsService.getSettingsObservable().subscribe(e => {
      if (e) {
        this.settings = e;
        this.textAreaStyleState.next(JSON.parse(JSON.stringify(e.textTools)));
      }
    });
  }

  private openNewTextNoteByClick = (e: MouseEvent, customcoords: boolean = false, annotType: AnnotationTypes | null) => {
    this.googleAnalyticsService.trackAction(GoogleAnalyticsActions.NOTE_CREATED, annotType ? annotType : 'unknown');
    const doc = this.currentDoc;
    if (doc && this.style) {
      const data: void | ICustomTextAreaState = doc.openNewTextNoteByClick(e, customcoords, this.style.fontSize, annotType);

      if (data) {
        data.style = this.style;
        this.stateSubj.next(data);
        // this.notesCreated++;
      }
    }
  }

  async convertAnnotationToEditableElement(e: Core.Annotations.Annotation): Promise<void> {
    const doc = this.currentDoc;
    if (!doc) {
      return;
    }

    const command = new TextAnnotationCommand(doc, e);
    await this.commandInvoker.ExecuteCommand(command);

    doc.scrollToBeginningOfAnotation(e as Core.Annotations.FreeTextAnnotation);

    const data = doc.convertAnnotationToEditableElement(e as Core.Annotations.FreeTextAnnotation);

    if (data) {
      await this.updateReadingPosition(e);

      // take note type
      // if current active note type not equal clicked
      // disable current mode and enable note double clicked mode
      if (this.activeNote !== null && data.annotType && data.annotType !== this.activeNote) {
        let modeToDisable = data.annotType === AnnotationTypes.FreeText ? AsgardFeatureNames.STICKY_NOTES : data.annotType === AnnotationTypes.StickyNote ? AsgardFeatureNames.TEXT_NOTES : null;
        // console.warn('convertAnnotationToEditableElement activeNote: ', this.activeNote);
        // console.warn('convertAnnotationToEditableElement modeToDisable: ', modeToDisable);
        if (modeToDisable !== null) {
          this.uiService.setItemUnActive(modeToDisable)
        }
      }
      this.activeNote = data.annotType;
      // state items to update
      const itemsUpdateState: AsgardFeatureNames[] = [];

      // update textnotes menu state - make it active if it doesnt
      if (!this.isActive) {
        if (this.activeNote === AnnotationTypes.FreeText) {
          itemsUpdateState.push(this.AsgardFeatureNames.TEXT_NOTES);
        } else if (this.activeNote === AnnotationTypes.StickyNote) {
          itemsUpdateState.push(this.AsgardFeatureNames.STICKY_NOTES);
        }
      }

      // if notes creation modes not enable
      // enable signle creation
      if(!uiState[this.AsgardFeatureNames.NOTE_CREATION_MODE_SINGLE].active && !uiState[this.AsgardFeatureNames.NOTE_CREATION_MODE_MULTI].active){
        itemsUpdateState.push(this.AsgardFeatureNames.NOTE_CREATION_MODE_SINGLE);
      }

      // if textnotes toolbar not opened - open it
      if (!this.openedToolbar) {
        itemsUpdateState.push(this.AsgardFeatureNames.TOOLBAR_MENU);
        itemsUpdateState.push(this.AsgardFeatureNames.NOTE_CREATION_MODE_SINGLE);
      }

      // update state only if necessary
      if (itemsUpdateState.length !== 0) {
        this.uiService.setItemActive(itemsUpdateState);
      }

      // console.warn('itemsUpdateState: ', itemsUpdateState);
      // console.warn('this.isActive: ', this.isActive);

      // pass text annotation data - to iframe
      this.stateSubj.next(data);

      // apply text annotation styles to text notes style menu
      this.textAreaStyleState.next(JSON.parse(JSON.stringify(data.style)));
    }
  }

  private isAnotClicked(e: MouseEvent): boolean {
    const annotationManager = this.annotationManager;
    if (!annotationManager) {
      return false;
    }
    let textAnot: Core.Annotations.Annotation | null = null;
    if (e) {
      textAnot = annotationManager.getAnnotationByMouseEvent(e);
      if (textAnot && isAnnotationType(textAnot, [AnnotationTypes.FreeText, AnnotationTypes.StickyNote], true)) {
        return true;
      }
    }
    return false;
  }

  private async drawTextAnnotation(): Promise<void> {
    let doc = this.currentDoc;

    // destory text note focus document in doc manager
    // we have all data in currentState object
    if (!doc && this.docMgrService.isFocusDocument && this.docMgrService.currentDoc
      && this.docMgrService.currentDoc.documentId === this.docId) {
      this.docMgrService.removeDocument(this.docId);
      doc = this.currentDoc;
    }
    // console.log(this.currentState, 'current state');
    if (doc && this.currentState && this.input) {
      // refresh state
      this.state = null;

      // clean text in iframe
      // this.input.innerText = '';

      // NOTE 13 JAN 2025 state should have the page of the note you're trying to add
      // send that along with it in case the current logic can't find the page
      const annot = await doc.drawTextAnnotation({...this.currentState, pageIdx: this.stateSubj.getValue()?.pageIdx ?? null});
      await this.updateReadingPosition(annot);

      // console.log('drawTextAnnotation stateSubj null');
      // this.stateSubj.next(null);
    }
    //  else if (doc && this.currentState && !this.currentState.text && this.state) {
    //   this.state = null;
    //   this.stateSubj.next(null);
    // }
  }

  textAreaStatus(): Observable<ICustomTextAreaState | null> {
    return this.textAreaState.asObservable();
  }

  keyDownHandler = (e: KeyboardEvent) => {
    this.updateSize();
    if (e.keyCode === 13 && this.frameDoc) {
      e.preventDefault();
      this.frameDoc.execCommand('insertHTML', false, '<br><br>');
      this.updateSize();
      return false;
    }
  }

  async removeFocusDoc(): Promise<void> {
    const doc = this.currentDoc;
    if (!doc && this.docMgrService.isFocusDocument && this.docMgrService.currentDoc &&
      this.docMgrService.currentDoc.documentId === this.docId) {
      if (this.state) {
        // await this.drawTextAnnotation();
        this.drawTextAnnotation();
        this.stateSubj.next(null);
      }
    }
  }

  /* REGION: style */
  focus(): void {
    setTimeout(() => {
      if (this.input && this.frame) {
        this.frame.focus();
        this.input.focus();
        if (this.input.innerText) {
          const empty = document.createElement('span');
          empty.setAttribute('id', this.frameCursorId);
          empty.innerHTML = '&nbsp;';
          this.input.appendChild(empty);
          if (this.frameWin && this.frameDoc) {
            const sel = this.frameWin.getSelection();
            const range = this.frameDoc.createRange();
            range.selectNodeContents(empty);
            range.collapse(true);
            if (sel) {
              sel.removeAllRanges();
              sel.addRange(range);
              if (empty) {
                empty.remove();
              }
            }
          }

        }
      }
    }, 200);
  }

  updateSize(): void {
    this.ngzone.run(() => {
      // const frameWrapper = $(`#${this.commonWrapper}`);
      const frameWrapper = document.querySelector(`#${this.commonWrapper}`);
      if (frameWrapper) {
        let diff = this.state && this.state.style && this.state.style.fontSize ? this.state.style.fontSize : 72;
        diff = diff * (this.state && this.state.zoom ? this.state.zoom : 1) * 2;
        // const input = $(`#${this.editorId}`, this.frameDoc);
        const input = this.frameDoc?.querySelector(`#${this.editorId}`);
        const height = input?.clientHeight || 0;
        const width = input?.clientWidth || 0;
        (frameWrapper as HTMLElement).style.height = height + diff + 'px';
        (frameWrapper as HTMLElement).style.width = width + diff + 'px';
      }
    });
  }

  textAreaStyleStatus(): Observable<ICustomTextNoteElStyle | null> {
    return this.textAreaStyleState.asObservable();
  }

  selectFont(e: ITextNoteFont): void {
    const value = this.textAreaStyleState.getValue();
    if (value) {
      value.fontFamily = e;
      this.textAreaStyleState.next(value);
    }
  }

  selectFontSize(e: number): void {
    const value = this.textAreaStyleState.getValue();
    if (value) {
      value.fontSize = e;
      this.textAreaStyleState.next(value);
    }
  }

  selectColor(e: PdfStrokeColor): void {
    const value = this.textAreaStyleState.getValue();
    if (value) {
      value.color = e;
      this.textAreaStyleState.next(value);
    }
  }

  selectFontType(e: TextNotesFontTypeStyle): void {
    const value = this.textAreaStyleState.getValue();
    if (value) {
      value.fontType = e;
      this.textAreaStyleState.next(value);
    }
  }

  private setDefaultStyles(): void {
    if (this.settings) {
      this.textAreaStyleState.next(JSON.parse(JSON.stringify(this.settings.textTools)));
    }
  }

  private addNotesCursorStyles(): void {
    // only on pdf files
    const cursorStyle = this.activeNote === AnnotationTypes.StickyNote ?
                        CustomDocviewerCursorStyles.stickynote : CustomDocviewerCursorStyles.textnote;
    const pdfs = this.docMgrService.getAllDocuments(CloudMimeTypesEnum.ApplicationPdf) as AsgardDocPdf[];
    pdfs.forEach(pdf => {
      if (pdf.isDocumentReady) {
        if (this.isActive) {
          updateCursorIcon(pdf.documentId, cursorStyle);
          this.updateIFrameDocCursor(cursorStyle);
          pdf.disableTextSelection();
          this.docToolsService.enableTool(ToolEventSource.Notes, PdfToolType.Pan);
        } else {
          resetCursorIcon(pdf.documentId, CustomDocviewerCursorStyles.stickynote);
          resetCursorIcon(pdf.documentId, CustomDocviewerCursorStyles.textnote);
          this.resetIFrameDocCursor();
          pdf.enableTextSelection();
          this.docToolsService.disableTool(ToolEventSource.Notes);
        }
      }
    });



    // if (this.currentDoc && this.isActive && this.currentDoc.isDocumentReady) {
    //   let cursorStyle = CustomDocviewerCursorStyles.textnote;
    //   if (this.activeNote === AnnotationTypes.StickyNote) {
    //     cursorStyle = CustomDocviewerCursorStyles.stickynote;
    //   }
    //   updateCursorIcon(this.currentDoc.documentId, cursorStyle);
    //   this.updateIFrameDocCursor(cursorStyle);
    //   this.currentDoc.disableTextSelection();
    //   this.docToolsService.enableTool(ToolEventSource.Notes, PdfToolType.Pan);

    // } else if (this.currentDoc && !this.isActive && this.currentDoc.isDocumentReady) {

    //   resetCursorIcon(this.currentDoc.documentId);
    //   this.resetIFrameDocCursor();
    //   this.currentDoc.enableTextSelection();
    //   this.docToolsService.disableTool(ToolEventSource.Notes);
    // }
  }

  private updateIFrameDocCursor(cursor: CustomDocviewerCursorStyles): void {
    if (!this.frameDoc) {
      return;
    }
    this.resetIFrameDocCursor();
    this.frameDoc.body.classList.add(`cursor-${cursor}`);
  }

  private resetIFrameDocCursor(): void {
    if (!this.frameDoc) {
      return;
    }
    const cursorClasses = Object.values(CustomDocviewerCursorStyles)
      .map(cursor => `cursor-${cursor}`);
    this.frameDoc.body.classList.remove(...cursorClasses);
  }

  private async updateReadingPosition(annot: Core.Annotations.Annotation | null): Promise<void> {
    const doc = this.currentDoc;
    if (!doc || !annot) {
      return;
    }
    const wordIndex = await doc.getWordIndexBeforeAnnot(annot);
    if (wordIndex) {
      await this.autosave.updateReadingPosition(doc.docHash, wordIndex, doc.getCurrentPageIdx());
    }
  }

  private createNoteIfNeeded(mouseEvent: MouseEvent, customCoords: boolean = false, annotType: AnnotationTypes | null): void {
    if (this.isActive) {
      this.openNewTextNoteByClick(mouseEvent, customCoords, annotType);
      return;
    }
    this.endNoteCreation();
  }

  private endNoteCreation(): void {
    this.uiService.setItemUnActive([AsgardFeatureNames.TEXT_NOTES, AsgardFeatureNames.STICKY_NOTES, AsgardFeatureNames.NOTE_CREATION_MODE_MULTI]);
    // this.uiService.setItemUnActive(AsgardFeatureNames.TEXT_NOTES);
    // this.uiService.setItemUnActive(AsgardFeatureNames.STICKY_NOTES);
  }

  private async saveFontSettings(partialSettings: Partial<ICustomTextNoteElStyle>): Promise<void> {
    const textToolsSettings = (this.settings || DefaultSettings).textTools;
    const merged = { ...textToolsSettings, ...partialSettings };
    return this.settingsService.saveSettings({ textTools: merged });
  }

  /* end region */
}
